package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCheckCheckIcon
import dev.lingvo.site.ds.LSkeleton
import dev.lingvo.site.ds.LStatTile
import dev.lingvo.site.ds.LWandSparklesIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/** The dialog title for whichever automation asked for a decision. */
@Composable
internal fun editorBulkConfirmTitle(confirmation: EditorBulkConfirmation?): String = when {
    confirmation == null -> t("editor.editor_bulk_confirm_surface.confirm", "Confirm")
    confirmation.intent == EditorBulkIntent.PRETRANSLATE -> tPlural(
        "editor.editor_bulk_confirm_surface.pre_translate_count",
        confirmation.count,
        "Pre-translate {count} string",
        "Pre-translate {count} strings",
    )

    else -> tPlural(
        "editor.editor_bulk_confirm_surface.approve_count",
        confirmation.count,
        "Approve {count} QA-clean string",
        "Approve {count} QA-clean strings",
    )
}

/**
 * The decision, with the evidence for it above and nothing else on screen.
 *
 * **Estimate before, receipt after.** The pre-translate half draws the server's own estimate for
 * the exact request the confirm button posts; the approve half draws a `dryRun` `decide-batch`,
 * which ran the same gate the write will. Neither number is computed here, because a second
 * implementation of "how many" is a second answer.
 *
 * The dialog closes when the store clears its pending confirmation, which happens on success only.
 * A refusal keeps it open with the reason in place — the reader is standing in front of the
 * decision, which is where the sentence explaining it belongs.
 */
@Composable
internal fun EditorBulkConfirmBody(
    automation: EditorQueueAutomationStore,
    onClose: () -> Unit,
) {
    val confirmation = automation.confirmation
    LaunchedEffect(confirmation == null) {
        if (confirmation == null) onClose()
    }
    DisposableEffect(automation) {
        onDispose { automation.dismissConfirmation() }
    }
    if (confirmation == null) return

    Column(EditorBulkConfirmBodyStyle.toModifier().attr("data-editor-bulk-confirm", confirmation.intent.name.lowercase())) {
        Span(EditorMetaStyle.toModifier().toAttrs()) { Text(confirmation.scopeSentence) }

        when (confirmation.intent) {
            EditorBulkIntent.PRETRANSLATE -> PretranslateEvidence(confirmation)
            EditorBulkIntent.APPROVE_CLEAN -> ApproveEvidence(confirmation)
        }

        confirmation.error?.let { reason ->
            LAlert(
                title = t(
                    "editor.editor_bulk_confirm_surface.this_cannot_run",
                    "This cannot run yet",
                ),
                description = reason,
                variant = LAlertVariant.DANGER,
                live = true,
            )
        }

        Row(
            Modifier.fillMaxWidth().gap(Space.SM),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            LButton(
                label = t("editor.editor_bulk_confirm_surface.cancel", "Cancel"),
                onClick = {
                    automation.dismissConfirmation()
                    onClose()
                },
                modifier = EditorCoarseTargetStyle.toModifier()
                    .attr("id", EDITOR_BULK_MODAL_INITIAL_FOCUS_ID)
                    .attr("data-editor-coarse-target", "close"),
                variant = LButtonVariant.GHOST,
                size = LButtonSize.SM,
            )
            when (confirmation.intent) {
                EditorBulkIntent.PRETRANSLATE -> LButton(
                    label = tPlural(
                        "editor.editor_bulk_confirm_surface.translate_count",
                        confirmation.count,
                        "Translate {count} string",
                        "Translate {count} strings",
                    ),
                    onClick = automation::confirmPretranslate,
                    modifier = Modifier.attr("data-editor-bulk-confirm-action", "pretranslate"),
                    variant = LButtonVariant.PRIMARY,
                    size = LButtonSize.SM,
                    leadingIcon = { LWandSparklesIcon() },
                    // **Priced, and not blocked — not "nothing has gone wrong yet".** Gating on
                    // `error == null` meant an ordinary transport failure of the POST greyed the
                    // button out permanently: nothing clears `error` except the press this gate had
                    // just made impossible, so the only way back was Cancel and a second pricing
                    // round trip. The estimate has to exist (an unpriced run is the one thing this
                    // dialog is for) and it has to be runnable; a failed attempt stays retryable.
                    enabled = !confirmation.loading &&
                        confirmation.estimate != null &&
                        confirmation.estimate.blockedReason == null,
                    loading = confirmation.running,
                )

                EditorBulkIntent.APPROVE_CLEAN -> LButton(
                    label = tPlural(
                        "editor.editor_bulk_confirm_surface.approve_action_count",
                        confirmation.count,
                        "Approve {count} string",
                        "Approve {count} strings",
                    ),
                    onClick = automation::confirmApproveClean,
                    modifier = Modifier.attr("data-editor-bulk-confirm-action", "approve"),
                    variant = LButtonVariant.PRIMARY,
                    size = LButtonSize.SM,
                    leadingIcon = { LCheckCheckIcon() },
                    enabled = !confirmation.loading && confirmation.count > 0,
                    loading = confirmation.running,
                )
            }
        }
    }
}

@Composable
private fun PretranslateEvidence(confirmation: EditorBulkConfirmation) {
    val estimate = confirmation.estimate
    if (estimate == null) {
        LSkeleton(
            modifier = Modifier.fillMaxWidth(),
            label = t("editor.editor_bulk_confirm_surface.pricing_this_run", "Pricing this run"),
        )
        return
    }
    val strings = estimate.perLocale.sumOf { it.strings }
    val memory = estimate.perLocale.sumOf { it.memoryPrefillable }
    Row(
        EditorQueueCountsStyle.toModifier().attr("data-editor-bulk-estimate", "true"),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        LStatTile(
            label = t("editor.editor_bulk_confirm_surface.strings", "Strings"),
            value = strings.toString(),
            compact = true,
        )
        LStatTile(
            label = t("editor.editor_bulk_confirm_surface.free_from_memory", "Free from memory"),
            value = memory.toString(),
            compact = true,
        )
        LStatTile(
            label = if (estimate.byok) {
                t("editor.editor_bulk_confirm_surface.credits_on_your_own_key", "Credits, on your own key")
            } else {
                t("editor.editor_bulk_confirm_surface.credits", "Credits")
            },
            value = estimate.totalCredits.toString(),
            compact = true,
        )
        LStatTile(
            label = t("editor.editor_bulk_confirm_surface.left_after_this", "Left after this"),
            // Clamped, as `PretranslateConfirm` and `PretranslateRun` already clamp the same
            // figure. A balance cannot go below zero, and "-292" is a number the workspace has
            // never had; the shortfall is said in words below instead.
            value = (estimate.creditsRemaining - estimate.totalCredits).coerceAtLeast(0).toString(),
            compact = true,
        )
    }
    // **The run costs more than the workspace has left, said before the press.** `blockedReason`
    // answers a broken provider, not an empty balance, so without this the confirm looked ordinary
    // and came back a 402 the reader had no warning of.
    val shortfall = estimate.totalCredits - estimate.creditsRemaining
    if (!estimate.byok && shortfall > 0) {
        LAlert(
            title = t(
                "editor.editor_bulk_confirm_surface.this_run_costs_more_than_is_left",
                "This run costs more than is left",
            ),
            description = tPlural(
                "editor.editor_bulk_confirm_surface.short_by_credits",
                shortfall,
                "It is short by {count} credit. Narrow the scope, translate manually, or ask an owner or admin about using a provider key.",
                "It is short by {count} credits. Narrow the scope, translate manually, or ask an owner or admin about using a provider key.",
            ),
            variant = LAlertVariant.WARNING,
        )
    }
    // The one thing an estimate cannot answer, said rather than implied: the tone choice is not
    // priced, because the estimate route ignores it.
    Span(EditorMetaStyle.toModifier().toAttrs()) {
        Text(
            t(
                "editor.editor_bulk_confirm_surface.machine_output_is_written_as_translated",
                "Machine output is written as translated, never approved, so every string still " +
                    "comes to you for review.",
            ),
        )
    }
}

@Composable
private fun ApproveEvidence(confirmation: EditorBulkConfirmation) {
    Span(EditorMetaStyle.toModifier().toAttrs()) {
        Text(
            t(
                "editor.editor_bulk_confirm_surface.each_string_is_checked_again",
                "Each string is checked again on the server; anything with a QA error or edited " +
                    "since you loaded it is skipped.",
            ),
        )
    }
    if (confirmation.previewNames.isEmpty()) return
    Ul(EditorBulkPreviewListStyle.toModifier().toAttrs()) {
        confirmation.previewNames.forEach { name ->
            Li { Text(name) }
        }
    }
    val remainder = confirmation.items.size - confirmation.previewNames.size
    if (remainder > 0) {
        Span(EditorMetaStyle.toModifier().toAttrs()) {
            Text(
                tf(
                    "editor.editor_bulk_confirm_surface.and_more",
                    "and {count} more",
                    "count" to remainder.toString(),
                ),
            )
        }
    }
}
